import React, { useState, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { remittanceAPI, RemittanceRecord, PaginationInfo, SearchParams } from '../services/api';
import SearchForm from './SearchForm';
import RemittanceTable from './RemittanceTable';
import GroupedRemittanceTable from './GroupedRemittanceTable';
import Pagination from './Pagination';
import { AlertCircle, Search, List, Users, Download } from 'lucide-react';

const RemittanceQuery: React.FC = () => {
  const [records, setRecords] = useState<RemittanceRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('list');

  // 將YYYY-MM-DD格式轉換為YYYY/MM/DD格式（後端API期望的格式）
  const formatDateForAPI = (dateString: string) => {
    if (!dateString) return undefined;
    return dateString.replace(/-/g, '/');
  };

  // 獲取資料的函數
  const fetchRemittances = useCallback(async (params: SearchParams = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      // 準備API參數，固定業務員為'莊家羚'
      const apiParams = {
        ...searchParams,
        ...params,
        agent_name: '莊家羚', // 固定業務員
        sort_by: 'payment_date',  // 按截款日期排序
        sort_order: 'desc' as const        // 降序排列
      };

      // 處理日期參數：將前端的YYYY-MM-DD格式轉換為後端的YYYY/MM/DD格式
      if (params.start_date) {
        apiParams.start_date = formatDateForAPI(params.start_date);
      }
      if (params.end_date) {
        apiParams.end_date = formatDateForAPI(params.end_date);
      }

      const response = await remittanceAPI.getRemittances(apiParams);
      
      if (response.status === 'success') {
        setRecords(response.data);
        setPagination(response.pagination);
        setHasSearched(true);
        
        if (response.data.length === 0) {
          toast.info('未找到符合條件的記錄');
        }
      } else {
        throw new Error('回應狀態異常');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || '載入資料失敗';
      setError(errorMessage);
      toast.error(errorMessage);
      setRecords([]);
      setPagination({
        current_page: 1,
        page_size: 20,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  // 搜尋處理
  const handleSearch = useCallback((params: Omit<SearchParams, 'page' | 'page_size'>) => {
    const newSearchParams = {
      ...params,
      page: 1,
      page_size: pagination.page_size,
    };
    setSearchParams(newSearchParams);
    fetchRemittances(newSearchParams);
  }, [pagination.page_size, fetchRemittances]);

  // 分頁處理
  const handlePageChange = (page: number) => {
    const newParams = {
      ...searchParams,
      page,
    };
    setSearchParams(newParams);
    fetchRemittances(newParams);
  };

  // 每頁筆數變更處理
  const handlePageSizeChange = (pageSize: number) => {
    const newParams = {
      ...searchParams,
      page: 1,
      page_size: pageSize,
    };
    setSearchParams(newParams);
    fetchRemittances(newParams);
  };

  // 匯出Excel處理
  const handleExport = useCallback(async () => {
    if (!hasSearched) {
      toast.warning('請先執行搜尋再匯出資料');
      return;
    }

    try {
      setLoading(true);
      
      // 準備匯出參數（排除分頁參數）
      const exportParams = { ...searchParams };
      delete exportParams.page;
      delete exportParams.page_size;
      
      // 處理日期格式
      if (exportParams.start_date) {
        exportParams.start_date = formatDateForAPI(exportParams.start_date);
      }
      if (exportParams.end_date) {
        exportParams.end_date = formatDateForAPI(exportParams.end_date);
      }
      
      // 固定業務員
      exportParams.agent_name = '莊家羚';
      exportParams.sort_by = 'payment_date';
      exportParams.sort_order = 'desc';

      const response = await remittanceAPI.exportToExcel(exportParams);
      
      // 創建下載連結
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 從響應標頭中獲取檔案名稱，或使用預設名稱
      const contentDisposition = response.headers['content-disposition'];
      let filename = '匯款記錄.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        // 生成帶時間戳的檔案名稱
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
        filename = `匯款記錄_${timestamp}.xlsx`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // 清理
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel檔案匯出成功');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || '匯出失敗';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [searchParams, hasSearched, formatDateForAPI]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* 頁面標題 */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">匯款記錄查詢系統</h1>
            <p className="mt-1 text-sm text-gray-500">
              查詢和管理莊家羚的匯款記錄資料
            </p>
          </div>
        </div>

        {/* 錯誤訊息 */}
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">載入失敗</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => fetchRemittances(searchParams)}
                    className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200"
                  >
                    重試
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 搜尋表單 */}
        <div className="mb-4 sm:mb-6">
          <SearchForm onSearch={handleSearch} loading={loading} />
        </div>

        {/* 顯示模式切換和匯出功能 */}
        {!loading && records.length > 0 && hasSearched && (
          <div className="mb-4 sm:mb-6">
            <div className="bg-white rounded-lg shadow-md p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="text-sm text-gray-600">
                  顯示模式
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                  {/* 顯示模式切換 */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        viewMode === 'list'
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <List className="h-4 w-4 mr-1" />
                      列表模式
                    </button>
                    <button
                      onClick={() => setViewMode('grouped')}
                      className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        viewMode === 'grouped'
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      客戶分組
                    </button>
                  </div>
                  
                  {/* 匯出按鈕 */}
                  <button
                    onClick={handleExport}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {loading ? '匯出中...' : '匯出Excel'}
                  </button>
                </div>
              </div>
              
              {/* 匯出說明 */}
              <div className="mt-2 text-xs text-gray-500">
                匯出當前搜尋條件下的所有記錄（包含：截款日、客戶名稱、匯款單位、銀行、分頁、備註）
              </div>
            </div>
          </div>
        )}

        {/* 統計資訊 - 移到表格前面，手機版改為單行顯示 */}
        {!loading && records.length > 0 && hasSearched && (
          <div className="mb-4 sm:mb-6">
            {/* 手機版 - 單行緊湊顯示 */}
            <div className="block sm:hidden bg-white rounded-lg shadow-md p-3">
              <div className="flex justify-between items-center text-xs">
                <div className="text-center flex-1">
                  <div className="text-gray-500">總記錄</div>
                  <div className="font-semibold text-blue-600">{pagination.total_count}</div>
                </div>
                <div className="text-center flex-1 border-l border-gray-200">
                  <div className="text-gray-500">本頁單位</div>
                  <div className="font-semibold text-green-600">
                    {records.reduce((sum, record) => sum + record.amount, 0).toLocaleString('zh-TW', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })}
                  </div>
                </div>
                <div className="text-center flex-1 border-l border-gray-200">
                  <div className="text-gray-500">頁數</div>
                  <div className="font-semibold text-purple-600">
                    {pagination.current_page}/{pagination.total_pages}
                  </div>
                </div>
              </div>
            </div>

            {/* 桌面版 - 原有的卡片式顯示 */}
            <div className="hidden sm:grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">總</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          總記錄數
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {pagination.total_count.toLocaleString()} 筆
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">金</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          當前頁面總單位
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {records.reduce((sum, record) => sum + record.amount, 0).toLocaleString('zh-TW', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">頁</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          當前頁數
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          第 {pagination.current_page} 頁 / 共 {pagination.total_pages} 頁
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 資料表格 */}
        <div className="mb-4 sm:mb-6">
          {viewMode === 'list' ? (
            <RemittanceTable records={records} loading={loading} />
          ) : (
            <GroupedRemittanceTable records={records} loading={loading} />
          )}
        </div>

        {/* 分頁 */}
        {!loading && records.length > 0 && hasSearched && pagination.total_pages > 1 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}

        {/* 空狀態提示 */}
        {!loading && !hasSearched && (
          <div className="text-center py-12">
            <Search className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">開始搜尋</h3>
            <p className="mt-1 text-sm text-gray-500">
              請使用上方搜尋表單來查詢莊家羚的匯款記錄
            </p>
          </div>
        )}
      </div>

      {/* Toast 通知 - 手機版優化位置 */}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        className="sm:!top-4 !top-2 !left-2 !right-2 sm:!left-auto sm:!right-4 sm:!w-auto !w-auto"
        toastClassName="!text-sm !rounded-md"
      />
    </div>
  );
};

export default RemittanceQuery; 